import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, KeyRound, ShieldAlert, Check, AlertCircle } from 'lucide-react';
import { authApi } from '@/api/client';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  ActiveSessionsCard,
  TrustedDevicesCard,
} from '@/components/settings/settings-account-section';

type SendState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * Security controls.
 *
 * Password change reuses the real reset-by-email flow (POST
 * /auth/forgot-password) — no new endpoint invented. MFA has no backend
 * contract yet and stays an honest "coming soon" row.
 *
 * **Sessions actives** used to be a "coming soon" badge HERE while the real,
 * fully-wired sessions table (GET /auth/sessions + revoke) sat two tabs away in
 * Compte. Same for the trusted devices it pairs with. So this panel told the
 * operator a feature did not exist while the product shipped it elsewhere.
 * Both cards now render here, where one goes looking for them, and Compte keeps
 * only identity + the danger zone (Notion, « Last push before going live » →
 * Settings → Security).
 */
export function SettingsSecuritySection() {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [state, setState] = useState<SendState>('idle');

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setState('sending');
    try {
      await authApi.forgotPassword(user.email);
      setState('sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">{t('settings.security.title')}</CardTitle>
        </div>
        <CardDescription>{t('settings.security.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('settings.security.passwordTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('settings.security.passwordBody')}</p>
          </div>
          {state === 'sent' ? (
            <span
              role="status"
              className="inline-flex shrink-0 items-center gap-1 text-sm text-cobalt-ink"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              {t('settings.security.passwordSent')}
            </span>
          ) : state === 'error' ? (
            <span
              role="alert"
              className="inline-flex shrink-0 items-center gap-1 text-sm text-destructive"
            >
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              {t('settings.saveError')}
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={handleResetPassword}
              disabled={state === 'sending' || !user?.email}
            >
              <KeyRound className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {t('settings.security.passwordCta')}
            </Button>
          )}
        </div>

        {/* La ligne « Sessions actives — bientôt disponible » a disparu : la
            vraie table est rendue juste dessous. */}

        <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('settings.security.mfaTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('settings.security.mfaBody')}</p>
          </div>
          <Badge variant="outline" className="shrink-0">
            <ShieldAlert className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t('settings.security.unavailable')}
          </Badge>
        </div>
      </CardContent>
    </Card>

    {/* Les deux cartes déménagées depuis Compte. Elles portent leurs propres
        requêtes (`/auth/sessions`, `/auth/trusted-devices`), donc elles ne se
        déclenchent que lorsque cet onglet est visité. */}
    <ActiveSessionsCard />
    <TrustedDevicesCard />
    </div>
  );
}
